import { useLang } from "@/lib/LanguageContext";

/**
 * Pricing — editorial "how commissions are priced" section.
 * Placed after Services so visitors read scope of offering first, then
 * understand the pricing philosophy, and are naturally guided into Contact.
 */
export default function Pricing() {
    const { t } = useLang();
    const p = t.pricing;

    const scrollToContact = () => {
        const el = document.getElementById("contact");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <section
            id="pricing"
            data-testid="pricing-section"
            className="py-20 lg:py-32 border-t border-hairline bg-ivory/60"
        >
            <div className="mx-auto max-w-screen-2xl px-6 md:px-12 lg:px-20">
                {/* Header */}
                <div className="grid grid-cols-12 gap-8 md:gap-16 mb-14 md:mb-20">
                    <div className="col-span-12 md:col-span-6">
                        <p
                            data-testid="pricing-eyebrow"
                            className="overline text-terracotta mb-6"
                        >
                            {p.eyebrow}
                        </p>
                        <h2 className="font-serif text-5xl lg:text-7xl font-light tracking-tight text-navy">
                            {p.title}
                        </h2>
                    </div>
                    <p className="col-span-12 md:col-span-6 self-end max-w-xl text-navy/70 leading-relaxed">
                        {p.intro}
                    </p>
                </div>

                {/* Body grid */}
                <div className="grid grid-cols-12 gap-8 md:gap-16 border-t border-hairline pt-14 md:pt-20">
                    {/* Left — price range statement */}
                    <div className="col-span-12 md:col-span-5">
                        <p
                            data-testid="pricing-range"
                            className="font-serif text-3xl md:text-4xl leading-snug text-navy"
                        >
                            {p.range}
                        </p>
                    </div>

                    {/* Right — included list */}
                    <div className="col-span-12 md:col-span-6 md:col-start-7">
                        <p className="overline text-navy/50 mb-6">
                            {p.includedTitle}
                        </p>
                        <ul
                            data-testid="pricing-included-list"
                            className="space-y-4"
                        >
                            {p.included.map((item, i) => (
                                <li
                                    key={item}
                                    data-testid={`pricing-included-${i}`}
                                    className="flex items-start gap-4 text-navy/85"
                                >
                                    <span className="mt-[10px] block h-[6px] w-[6px] shrink-0 rounded-full bg-terracotta" />
                                    <span className="leading-relaxed">
                                        {item}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* CTA block */}
                <div className="mt-16 md:mt-24 border-t border-hairline pt-14 md:pt-20 grid grid-cols-12 gap-8 md:gap-16">
                    <div className="col-span-12 md:col-span-8">
                        <p className="font-serif text-2xl md:text-3xl leading-snug text-navy mb-8">
                            {p.cta}
                        </p>
                        <button
                            type="button"
                            data-testid="pricing-cta"
                            onClick={scrollToContact}
                            className="group inline-flex items-center gap-3 border border-navy px-8 py-4 text-navy hover:bg-navy hover:text-ivory transition-colors duration-300"
                        >
                            <span className="uppercase tracking-[0.2em] text-xs">
                                {p.ctaButton}
                            </span>
                            <span className="transition-transform group-hover:translate-x-1">
                                →
                            </span>
                        </button>
                    </div>
                    <div className="col-span-12 md:col-span-4 md:pl-8 md:border-l md:border-hairline space-y-6">
                        <p
                            data-testid="pricing-note"
                            className="text-navy/70 leading-relaxed italic"
                        >
                            {p.note}
                        </p>
                        <p
                            data-testid="pricing-base"
                            className="text-navy/55 text-sm leading-relaxed"
                        >
                            {p.base}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}
